import Colors from "modules/Colors";
import Sty from "modules/Sty";
import { clsx, makeKey, styx } from "modules/Utils";
import { CSSProperties, useEffect, useRef, useState, useMemo } from "react";
import SwitchSymphony from "elements/SwitchSymphony";
import Icon from "./Icon";
import InputSymphony, { SelectOptionItem } from "./InputSymphony";
import $ from 'jquery';
import Loader from "./Loader";

export type RowSelection = {
    mode: "singleRow" | "multiRow";
    headerCheckbox: boolean;
}

export type ColDef = {
    field?: string;
    title?: string;
    editable?: boolean;
    cellEditor?: string;
    cellEditorParams?: Record<string, any>;
    comparator?: (valueA: string, valueB: string) => number;
    renderCell?: (row: any, column: ColDef) => React.ReactNode;
    flex?: number;
    minWidth?: number;
    isSortable?: boolean;
    isPro?: boolean;
}

interface TableElementProps {
    className: string;
    styleTableElement?: CSSProperties;
    rowData: any[];
    columnDefs: ColDef[];
    rowSelection?: RowSelection;
    onSelectionChange?: (selectedRows: any[]) => void;
    pagination?: boolean;
    paginationPageSize?: number;
    paginationPageSizeSelector?: number[];
    onPageSizeChange?: (pageSize: number) => void;
    onClickRow?: (row: any) => void;
    setSelectedRows?: (selectedRows: any[]) => void;
    rowsTitle?: string;
    minWidth?: number;
    rowsTotalOverride?: number;
    onClickRightArrow?: () => void;
    onClickRightArrowAsync?: () => Promise<void>;
}

const TableElement = ({
    className,
    styleTableElement,
    rowData,
    columnDefs,
    rowSelection,
    onSelectionChange,
    pagination = true,
    paginationPageSize = 10,
    paginationPageSizeSelector = [10, 25, 50],
    onPageSizeChange,
    onClickRow,
    setSelectedRows: setSelectedRowsProp,
    rowsTitle = 'rows',
    minWidth = 900,
    rowsTotalOverride,
    onClickRightArrow,
    onClickRightArrowAsync,
}: TableElementProps) => {
    const [isLoadingRightArrow, setIsLoadingRightArrow] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [selectedRows, setSelectedRows] = useState<any[]>([])
    const [itemsPerPage, setItemsPerPage] = useState(paginationPageSize)
    /** Track current sort state */
    const [sortState, setSortState] = useState<{
        field: string;
        direction: 'asc' | 'desc' | null;
    }>({ field: '', direction: null })
    /** Track if horizontal scrolling is needed */
    const [showScrollIndicator, setShowScrollIndicator] = useState<boolean>(false)
    /** Track if we've scrolled right and need to show left indicator */
    const [showLeftScrollIndicator, setShowLeftScrollIndicator] = useState<boolean>(false)
    /** Reference to the container element */
    const containerRef = useRef<HTMLDivElement>(null)

    /** Check if scrolling is needed on mount and resize */
    useEffect(() => {
        const checkForScrollbar = () => {
            if (containerRef.current) {
                const { scrollWidth, clientWidth } = containerRef.current
                setShowScrollIndicator(scrollWidth > clientWidth)
            }
        }

        checkForScrollbar()
        window.addEventListener('resize', checkForScrollbar)

        return () => {
            window.removeEventListener('resize', checkForScrollbar)
        }
    }, [rowData, columnDefs])

    // Update scroll indicators when scrolling
    const handleScroll = () => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = containerRef.current
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth
            const isAtStart = scrollLeft <= 0

            // Update right indicator
            if (isAtEnd && showScrollIndicator) {
                setShowScrollIndicator(false)
            } else if (!isAtEnd && !showScrollIndicator && scrollWidth > clientWidth) {
                setShowScrollIndicator(true)
            }

            // Update left indicator
            if (isAtStart && showLeftScrollIndicator) {
                setShowLeftScrollIndicator(false)
            } else if (!isAtStart && !showLeftScrollIndicator) {
                setShowLeftScrollIndicator(true)
            }
        }
    }

    /** Calculate pagination values */
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage

    /** Sort and paginate data */
    const sortedData = useMemo(() => {
        if (!sortState.direction || !sortState.field) return rowData

        return [...rowData].sort((a, b) => {
            const column = columnDefs.find(col => col.field === sortState.field)
            const valueA = a[sortState.field]
            const valueB = b[sortState.field]

            if (column?.comparator) {
                return sortState.direction === 'asc'
                    ? column.comparator(valueA, valueB)
                    : column.comparator(valueB, valueA)
            }

            if (valueA < valueB) return sortState.direction === 'asc' ? -1 : 1
            if (valueA > valueB) return sortState.direction === 'asc' ? 1 : -1
            return 0
        })
    }, [rowData, sortState, columnDefs])

    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem)
    const totalPages = Math.ceil((rowsTotalOverride ?? rowData.length) / itemsPerPage)

    const handleRowSelection = (row: any) => {
        let newSelection: any[] = []

        if (rowSelection?.mode === 'singleRow') {
            newSelection = [row]
        } else if (rowSelection?.mode === 'multiRow') {
            const isSelected = selectedRows.some(selected => selected === row)
            newSelection = isSelected
                ? selectedRows.filter(selected => selected !== row)
                : [...selectedRows, row]
        }

        setSelectedRows(newSelection)
        setSelectedRowsProp?.(newSelection)
        onSelectionChange?.(newSelection)
    }

    const handleHeaderCheckboxChange = () => {
        if (!rowSelection?.headerCheckbox || rowSelection.mode !== 'multiRow') {
            return
        }

        const newSelection = selectedRows.length === currentItems.length ? [] : [...currentItems]
        setSelectedRows(newSelection)
        setSelectedRowsProp?.(newSelection)
        onSelectionChange?.(newSelection)
    }

    const handleSort = (column: ColDef) => {
        if (!column.isSortable) {
            return
        }

        setSortState(prev => ({
            field: column.field ?? prev.field,
            direction: prev.field === column.field && prev.direction === 'asc'
                ? 'desc'
                : 'asc'
        }))
    }

    const getScrollIndicatorHeight = () => {
        const tableClassName = className ? `.${className} .tableElement` : '.tableElement'
        const height = ($(tableClassName).height() || 0) + 2

        return {
            height
        }
    }

    const renderCheckboxColumn = () => {
        if (!rowSelection) {
            return null;
        }

        if (!rowSelection.headerCheckbox) {
            return (
                <div style={{ width: 50 }} />
            )
        }

        return (
            <div className="tableCell" style={styx(sty.tableCell, sty.checkboxColumn)}>
                <SwitchSymphony
                    className="tableHeaderCheckbox"
                    type="checkbox"
                    checked={selectedRows.length === currentItems.length}
                    onChange={handleHeaderCheckboxChange}
                />
            </div>
        )
    }

    const renderHeader = () => {
        return (
            <div className="tableHeader" style={sty.tableHeader}>
                {renderCheckboxColumn()}
                {columnDefs.map((column) => {
                    const {
                        title,
                        isPro,
                        isSortable,
                        flex,
                        minWidth,
                        field,
                    } = column
                    return (
                        <div
                            key={field}
                            className="tableHeaderCell"
                            style={styx(
                                sty.tableHeaderCell,
                                { flex: flex ?? 1, minWidth: minWidth ?? 'auto', flexShrink: 0 },
                                isSortable && sty.sortableHeader
                            )}
                            onClick={() => isSortable && handleSort(column)}
                        >
                            <div>{title ?? field}</div>
                            {isPro &&
                                <Icon
                                    className="tableHeaderCellIcon"
                                    icon="gem"
                                    color="purple"
                                    size={16}
                                />
                            }
                            {column.isSortable &&
                                <Icon
                                    className="tableHeaderCellIcon"
                                    icon={sortState.field === field ?
                                        sortState.direction === 'asc' ? 'chevronUp' : 'chevronDown'
                                        :
                                        'chevronUp'
                                    }
                                    color={sortState.field === field ? Colors.purple : Colors.grey}
                                />
                            }
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderCell = (row: any, column: ColDef) => {
        const value = column.field ? row[column.field] : row

        if (typeof value === 'boolean') {
            return (
                <SwitchSymphony
                    className="tableCellSwitch"
                    checked={value}
                    type="checkbox"
                    disabled
                />
            )
        }

        return value
    }

    const renderRows = () => {
        return currentItems.map((row, index) => {
            const isSelected = selectedRows.includes(row)

            return (
                <div
                    key={makeKey(row, index)}
                    className="tableRow"
                    style={styx(
                        sty.tableRow,
                        {
                            cursor: onClickRow ? 'pointer' : 'default',
                            backgroundColor: isSelected ? Colors.purpleLightest : 'white',
                        },
                        index === currentItems.length - 1 && {
                            borderBottom: 'none',
                            borderBottomLeftRadius: 7,
                            borderBottomRightRadius: 7,
                        }
                    )}
                    onClick={() => onClickRow?.(row)}
                >
                    {rowSelection?.mode === 'multiRow' && (
                        <div
                            className="tableCell"
                            style={styx(sty.tableCell, sty.checkboxColumn)}
                        >
                            <SwitchSymphony
                                className="tableCellSwitch"
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleRowSelection(row)}
                            />
                        </div>
                    )}
                    {columnDefs.map((column) => (
                        <div
                            key={makeKey(column.field, index)}
                            className="tableCell"
                            style={styx(
                                sty.tableCell,
                                { flex: column.flex ?? 1, minWidth: column.minWidth ?? 'auto', flexShrink: 0 }
                            )}
                        >
                            {column.renderCell ?
                                column.renderCell(row, column)
                                :
                                renderCell(row, column)
                            }
                        </div>
                    ))}
                </div>
            )
        })
    }

    const renderPaginationControls = () => {
        return (
            <div className="paginationControls" style={sty.paginationControls}>
                <Icon
                    className="paginationButton"
                    icon="chevronLeft"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    color={currentPage === 1 ? Colors.grey : Colors.purple}
                    disabled={currentPage === 1}
                />
                <div className="paginationInfo" style={Sty.text14grey}>
                    Page {currentPage} of {totalPages}
                </div>

                {isLoadingRightArrow ?
                    <Loader />
                    :
                    <Icon
                        className="paginationButton"
                        icon="chevronRight"
                        onClick={async () => {
                            const nextPage = Math.min(currentPage + 1, totalPages);
                            const nextPageLastItemIndex = nextPage * itemsPerPage;

                            // Only call the async function if we need more data AND we haven't loaded it yet
                            if (onClickRightArrowAsync && rowsTotalOverride && nextPageLastItemIndex > rowData.length && rowData.length < rowsTotalOverride) {
                                setIsLoadingRightArrow(true);
                                await onClickRightArrowAsync();
                                setIsLoadingRightArrow(false);
                            }

                            if (onClickRightArrow) {
                                onClickRightArrow();
                            }

                            setCurrentPage(nextPage);
                        }}
                        color={isRightArrowDisabled() ? Colors.grey : Colors.purple}
                        disabled={isRightArrowDisabled()}
                    />
                }
            </div>
        )
    }

    const renderPagination = () => {
        if (!pagination) {
            return null
        }

        return (
            <div className="tablePagination" style={sty.tablePagination}>
                <InputSymphony
                    className="paginationInput"
                    value={itemsPerPage.toString()}
                    onOptionSelect={(option) => {
                        setItemsPerPage(Number(option.value))
                        onPageSizeChange?.(Number(option.value))
                    }}
                    isSelectDropdown
                    options={paginationPageSizeSelector.map(size => ({
                        title: `${size} per page`,
                        value: size.toString()
                    })) as SelectOptionItem[]}
                    maxWidth={180}
                    styleInput={{ ...Sty.text14grey, paddingTop: 13 }}
                />
                {rowData.length > 0 &&
                    <div style={Sty.text14grey}>
                        {rowsTotalOverride ?? rowData.length} {rowsTitle} total
                    </div>
                }
                {renderPaginationControls()}
            </div>
        )
    }

    const isRightArrowDisabled = () => {
        // If rowsTotalOverride is provided, use it to determine if more data is available
        if (rowsTotalOverride !== undefined) {
            return currentPage >= totalPages;
        }

        // If no override is provided, use the standard logic based on current page and total pages
        return currentPage >= totalPages;
    }

    return (
        <>
            <style>
                {`
                    .tableRow:hover {
                        background-color: ${Colors.greyLighter} !important;
                    }
                `}
            </style>
            <div
                className={clsx("tableElementContainerWrapper", className)}
                style={sty.tableElementContainerWrapper}
            >
                <div
                    className="tableElementContainer"
                    style={sty.tableElementContainer}
                    ref={containerRef}
                    onScroll={handleScroll}
                >
                    <div
                        className="tableElement"
                        style={styx(
                            sty.tableElement,
                            { minWidth },
                            styleTableElement
                        )}
                    >
                        {renderHeader()}
                        <div className="tableBody" style={sty.tableBody}>
                            {renderRows()}
                        </div>
                    </div>
                </div>
                {renderPagination()}
                {showScrollIndicator &&
                    <div
                        className="scrollIndicatorRight"
                        style={styx(
                            sty.scrollIndicatorRight,
                            getScrollIndicatorHeight()
                        )}
                    />
                }
                {showLeftScrollIndicator &&
                    <div
                        className="scrollIndicatorLeft"
                        style={styx(
                            sty.scrollIndicatorLeft,
                            getScrollIndicatorHeight()
                        )}
                    />
                }
            </div>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    tableElementContainerWrapper: {
        position: 'relative',
    },
    tableElementContainer: {
        width: '100%',
        overflowX: 'scroll',
        scrollbarWidth: 'none',
    },
    tableElement: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: Sty.border,
        borderRadius: 8,
        backgroundColor: 'white',
        // overflowY: 'hidden',
    },
    tableHeader: {
        display: 'flex',
        ...Sty.text16grey,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottom: Sty.border,
    },
    tableHeaderCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 16,
    },
    tableBody: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    tableRow: {
        display: 'flex',
        borderBottom: Sty.border,
        width: '100%',
    },
    tableCell: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        padding: 16,
        minWidth: 48,
        ...Sty.text14grey,
    },
    checkboxColumn: {
        flex: 'none',
        width: 50,
        minWidth: 50,
        justifyContent: 'center'
    },
    tablePagination: {
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 16,
        gap: 24,
        flexWrap: 'wrap',
    },
    paginationControls: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    paginationInfo: {
        color: Colors.greyDark
    },
    sortableHeader: {
        cursor: 'pointer',
        userSelect: 'none'
    },

    scrollIndicatorRight: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: 30,
        pointerEvents: 'none',
        background: 'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.1))',
    },
    scrollIndicatorLeft: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: 30,
        pointerEvents: 'none',
        background: 'linear-gradient(to left, rgba(0,0,0,0), rgba(0,0,0,0.1))',
    }
}

export default TableElement;